import { useState } from 'react';

// ─── Peer percentile ─────────────────────────────────────────────────────────

function peerTopPct(score) {
  // Higher score = better = lower "top X%" number
  if (score >= 88) return 5;
  if (score >= 78) return 12;
  if (score >= 68) return 22;
  if (score >= 58) return 34;
  if (score >= 48) return 48;
  if (score >= 38) return 62;
  if (score >= 28) return 76;
  return 88;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(val, currencyCode) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) {
    return (
      (n < 0 ? '−' : '') +
      new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode, maximumFractionDigits: 1 })
        .format(Math.abs(n) / 1_000_000) + 'M'
    );
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currencyCode, maximumFractionDigits: 0,
  }).format(n);
}

function scoreLabel(n) {
  if (n >= 75) return { text: 'Strong Sellability', cls: 'badge-green' };
  if (n >= 50) return { text: 'Moderate Sellability', cls: 'badge-amber' };
  return { text: 'Needs Work', cls: 'badge-red' };
}

// ─── Component ───────────────────────────────────────────────────────────────

function ResultsScreen({ result, currency, onGetDeeper, onReset }) {
  const { ebitda, score, multiple, valuation } = result;
  const scoreNum  = parseFloat(score);
  const ebitdaNum = parseFloat(ebitda);
  const topPct    = peerTopPct(scoreNum);
  const markerPct = 100 - topPct;

  const { text: badgeText, cls: badgeCls } = scoreLabel(scoreNum);

  return (
    <div className="results-screen">

      {/* ── Disclaimer ── */}
      <div className="disclaimer-banner" style={{ marginBottom: 24 }}>
        <strong>Indicative estimate only.</strong> Not financial, legal, or investment advice.
        Consult a qualified professional before making any business decision.
      </div>

      {/* ── Valuation range headline ── */}
      <div className="rs-range-block">
        <p className="rs-eyebrow">Estimated Agency Value</p>
        <p className="rs-range-headline">
          {fmt(valuation.low, currency)}&nbsp;&ndash;&nbsp;{fmt(valuation.high, currency)}
        </p>
        <p className="rs-range-sub">
          {fmt(ebitdaNum, currency)} EBITDA &nbsp;·&nbsp; {parseFloat(multiple.low).toFixed(1)}×&ndash;{parseFloat(multiple.high).toFixed(1)}× multiple
        </p>
      </div>

      {/* ── Sellability badge ── */}
      <div className="rs-badge-row">
        <span className={`rs-badge ${badgeCls}`}>{badgeText}</span>
        <span className="rs-score-label">{scoreNum.toFixed(0)} / 100 Sellability Index</span>
      </div>

      {/* ── Peer comparison bar ── */}
      <div className="rs-peer-block">
        <p className="rs-peer-headline">
          Your agency is in the <strong>top {topPct}%</strong> of agencies we've assessed
        </p>
        <div className="rs-peer-bar-wrap">
          <div className="rs-peer-bar-track">
            <div
              className="rs-peer-bar-fill"
              style={{ width: `${markerPct}%` }}
            />
            <div
              className="rs-peer-bar-marker"
              style={{ left: `${markerPct}%` }}
            >
              <div className="rs-peer-bar-tip">You</div>
            </div>
          </div>
          <div className="rs-peer-bar-labels">
<span>Needs work</span><span>Industry average</span><span>Top performer</span>
          </div>
        </div>
      </div>

      {/* ── Breakdown strip ── */}
      <div className="rs-breakdown">
        <div className="rs-breakdown-item">
          <span className="rs-breakdown-label">Conservative</span>
          <span className="rs-breakdown-value">{fmt(valuation.low, currency)}</span>
        </div>
        <div className="rs-breakdown-divider" />
        <div className="rs-breakdown-item">
          <span className="rs-breakdown-label">Mid-point</span>
          <span className="rs-breakdown-value rs-breakdown-mid">{fmt(valuation.mid, currency)}</span>
        </div>
        <div className="rs-breakdown-divider" />
        <div className="rs-breakdown-item">
          <span className="rs-breakdown-label">Optimistic</span>
          <span className="rs-breakdown-value">{fmt(valuation.high, currency)}</span>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="rs-cta-block">
        <p className="rs-cta-headline">Ready to go deeper?</p>
        <p className="rs-cta-sub">
          Add your name to our list of people interested in selling their business.
        </p>

        <button
          className="btn"
          onClick={onGetDeeper}
          style={{ marginTop: 16 }}
        >
          Add me to the list →
        </button>
      </div>

    </div>
  );
}

export default ResultsScreen;
